/**
 * Receipts RTK Query slice: the vendor's Tax Compliance page (#495 "Per-unit tax
 * basis on inventory: the Paid Tax toggle becomes the Reg 1660 election
 * record"; #1272 "WEB: receipts reviewed as a batch, signed together";
 * docs/plans/taxes.md, The vendor side 2; docs/plans/receipts-redesign.md,
 * Decisions 14 to 25). A vendor names a drop of receipts they uploaded,
 * which the backend reads in the background and settles once its last file
 * is read (Reading uploads in the background), and reads how their uploads
 * stand; lists their receipts; settles the receipts of their batch that need
 * nothing when the review opens; reads what the batch's screens show; adds the batch to the
 * inventory, which writes its gear; reads the settled receipts and signs
 * them with one signature; deletes one, signed too; and reads the units
 * of their inventory the signed ones show the tax paid on.
 *
 * Every endpoint runs the three-stage schema pipeline (rawResponseSchema
 * envelope, named unwrap, responseSchema payload); paths come from
 * RECEIPT_ROUTES. The settle and Add to inventory make and change items, and
 * a signature changes the list and the tax paid, so each invalidates what it
 * changed, the listing and inventory caches included.
 */
import type { Dispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  BatchEnvelopeSchema,
  BatchSchema,
  BatchSettledEnvelopeSchema,
  BatchSettledSchema,
  RECEIPT_ROUTES,
  RECEIPT_SOCKET_EVENTS,
  ReadyReceiptsEnvelopeSchema,
  ReadyReceiptsSchema,
  ReceiptUploadChangedSchema,
  ReceiptUploadEnvelopeSchema,
  ReceiptUploadListEnvelopeSchema,
  ReceiptUploadListSchema,
  ReceiptUploadSchema,
  SignedReceiptsEnvelopeSchema,
  SignedReceiptsSchema,
  TaxPaidEnvelopeSchema,
  TaxPaidSchema,
  VendorReceiptEnvelopeSchema,
  VendorReceiptListEnvelopeSchema,
  VendorReceiptPageSchema,
  VendorReceiptSchema,
  unwrapBatch,
  unwrapBatchSettled,
  unwrapReadyReceipts,
  unwrapReceiptUpload,
  unwrapReceiptUploadList,
  unwrapSignedReceipts,
  unwrapTaxPaid,
  unwrapVendorReceipt,
  unwrapVendorReceiptPage,
  namedToQuery,
  receiptIdsToQuery,
  type AddToInventoryRequest,
  type Batch,
  type BatchQuery,
  type BatchSettled,
  type ListReceiptsQuery,
  type ReadyReceipts,
  type ReceiptUpload,
  type SignReceiptsRequest,
  type SignedReceipts,
  type TaxPaid,
  type UploadReceiptsRequest,
  type VendorReceipt,
  type VendorReceiptPage,
} from '@gearghost/api-schemas/receipts';
import axiosBaseQuery from './axiosBaseQuery';
import { onSchemaFailure } from './onSchemaFailure';
import { inventoryItemsApi } from './inventoryItemsApi';
import { listingsApi } from './listingsApi';
import { subscribeToSocket } from '@/redux/socketContext';
import { createLogger } from '@/utils/logger';

const log = createLogger('receiptsApi');

/**
 * What an inventory or listing save changes here: the gear needing a
 * receipt (TaxPaid), what the batch matches against the vendor's gear, and
 * the gear each receipt shows by name. The store marks them stale when one
 * of those saves succeeds (store.ts); a receipt's file never changes, so it
 * is not among them.
 */
export const RECEIPT_TAGS_GEAR_CHANGES = ['TaxPaid', 'Batch', 'ReadyReceipts', 'Receipts'] as const;

/** A receipt's file as the page shows it: the browser's own copy, and how to show it. */
export type ReceiptFileCopy = { url: string; kind: 'image' | 'document' };

/**
 * The inventory and listing caches the settle, Add to inventory or a
 * signature changes (items made or counted, stock, the sales-tax filter),
 * and an import through the vendor API as it makes items and listings
 * (useVendorImportSocketSync).
 */
export const invalidateInventoryAndListings = (dispatch: Dispatch) => {
  dispatch(inventoryItemsApi.util.invalidateTags(['Inventory', 'InventoryDetails']));
  dispatch(listingsApi.util.invalidateTags(['VendorListings', 'ListingDetail', 'Listings']));
};

export const receiptsApi = createApi({
  reducerPath: 'receiptsApi',
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ['Receipts', 'TaxPaid', 'Batch', 'ReadyReceipts', 'ReceiptUploads'],
  // Every answer is kept until something marks it stale, never dropped for
  // going unused: the vendor goes back and forth between receipts, and none
  // of this changes unless a receipt action (this slice's tags), an upload
  // finishing (useReceiptUploadSocketSync) or a gear save
  // (RECEIPT_TAGS_GEAR_CHANGES) changes it. A sign-out reloads the page, so
  // nothing is kept for the next person (docs/plans/receipts-redesign.md,
  // Requests the receipt pages send 3, 5, 8 and 10).
  keepUnusedDataFor: Infinity,
  endpoints: (builder) => ({
    // A page of the vendor's receipts: the parameters the page sets, the rest left to the server's defaults.
    getReceipts: builder.query<VendorReceiptPage, ListReceiptsQuery>({
      query: (query) => {
        const params = new URLSearchParams();
        Object.entries(query).forEach(([name, value]) => {
          if (value !== undefined && value !== '') params.set(name, String(value));
        });
        return `${RECEIPT_ROUTES.receipts}?${params.toString()}`;
      },
      rawResponseSchema: VendorReceiptListEnvelopeSchema,
      transformResponse: unwrapVendorReceiptPage,
      responseSchema: VendorReceiptPageSchema,
      providesTags: ['Receipts'],
    }),

    getReceipt: builder.query<VendorReceipt, number>({
      query: (receiptId) => RECEIPT_ROUTES.receipt(receiptId),
      rawResponseSchema: VendorReceiptEnvelopeSchema,
      transformResponse: unwrapVendorReceipt,
      responseSchema: VendorReceiptSchema,
      providesTags: ['Receipts'],
    }),

    // A receipt's own file, downloaded once and shown from the browser's copy
    // (Decision 63): the API refuses to be framed, and a copy in the page's
    // memory, never on disk, is not the API's. One signed-in request, counted
    // against the vendor's own budget. No tag: a receipt's file never
    // changes, since a replacement is a receipt of its own. The copy is
    // released when its cache entry goes.
    // no responseSchema — the answer is the file itself, a Blob, opaque to the page.
    getReceiptFile: builder.query<ReceiptFileCopy, number>({
      query: (receiptId) => ({ url: RECEIPT_ROUTES.file(receiptId), responseType: 'blob' }),
      transformResponse: (file: Blob): ReceiptFileCopy => ({
        url: URL.createObjectURL(file),
        kind: file.type.startsWith('image/') ? 'image' : 'document',
      }),
      onQueryStarted: async (receiptId, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          // The page shows that the file could not be loaded.
          log.warn({ err, receiptId }, 'the receipt file could not be loaded');
        }
      },
      onCacheEntryAdded: async (receiptId, { cacheDataLoaded, cacheEntryRemoved }) => {
        let copy: ReceiptFileCopy;
        try {
          ({ data: copy } = await cacheDataLoaded);
        } catch (err) {
          // The entry went before a file came: there is no copy to release.
          log.debug({ err, receiptId }, 'the receipt file entry went before its file came');
          return;
        }
        await cacheEntryRemoved;
        URL.revokeObjectURL(copy.url);
      },
    }),

    getTaxPaid: builder.query<TaxPaid, void>({
      query: () => RECEIPT_ROUTES.taxPaid,
      rawResponseSchema: TaxPaidEnvelopeSchema,
      transformResponse: unwrapTaxPaid,
      responseSchema: TaxPaidSchema,
      providesTags: ['TaxPaid'],
    }),

    // What the batch's screens show, with the receipts the vendor set aside
    // to wait for a fix or skipped left out, and what they bought with them,
    // and those showing no payment they use and the files they keep put in
    // (Decisions 74 and 87); the settle, Add to inventory and a signature
    // change it, so the tag drops then.
    getBatch: builder.query<Batch, BatchQuery>({
      query: ({ wait, use, keep, named, scope }) => {
        const search = new URLSearchParams([
          ...(wait.length === 0 ? [] : [['wait', receiptIdsToQuery(wait)]]),
          ...(use.length === 0 ? [] : [['use', receiptIdsToQuery(use)]]),
          ...(keep.length === 0 ? [] : [['keep', receiptIdsToQuery(keep)]]),
          // What the vendor said each line is on Name your gear, a model or their own item, which judges Your stock and New gear (Decisions 109 and 112).
          ...(named.length === 0 ? [] : [['named', namedToQuery(named)]]),
          // Just the receipts the listings need (Decision 96); none for the whole batch.
          ...(scope === undefined ? [] : [['scope', scope]]),
        ]).toString();
        return search === '' ? RECEIPT_ROUTES.batch : `${RECEIPT_ROUTES.batch}?${search}`;
      },
      rawResponseSchema: BatchEnvelopeSchema,
      transformResponse: unwrapBatch,
      responseSchema: BatchSchema,
      providesTags: ['Batch'],
    }),

    // The settled receipts not yet signed, with the gear each points at.
    getReadyReceipts: builder.query<ReadyReceipts, void>({
      query: () => RECEIPT_ROUTES.ready,
      rawResponseSchema: ReadyReceiptsEnvelopeSchema,
      transformResponse: unwrapReadyReceipts,
      responseSchema: ReadyReceiptsSchema,
      providesTags: ['ReadyReceipts'],
    }),

    // Names every file of a drop already sent to the receipts folder; the
    // backend reads them in the background and settles the batch once the
    // last is read (Reading uploads in the background 2 and 4). The banners'
    // list is read again, so the new upload shows at once.
    uploadReceipts: builder.mutation<ReceiptUpload, UploadReceiptsRequest>({
      query: (body) => ({ url: RECEIPT_ROUTES.uploads, method: 'POST', body }),
      rawResponseSchema: ReceiptUploadEnvelopeSchema,
      transformResponse: unwrapReceiptUpload,
      responseSchema: ReceiptUploadSchema,
      invalidatesTags: ['ReceiptUploads'],
    }),

    // What Tax Compliance's banners show: the uploads still being read
    // (Decision 56) and those with refused files not dismissed (Decision 58).
    getReceiptUploads: builder.query<ReceiptUpload[], void>({
      query: () => RECEIPT_ROUTES.uploads,
      rawResponseSchema: ReceiptUploadListEnvelopeSchema,
      transformResponse: unwrapReceiptUploadList,
      responseSchema: ReceiptUploadListSchema,
      providesTags: ['ReceiptUploads'],
      // The read job pushes an upload as it stands after each file
      // (RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED), written into the list here
      // while it is in use, as RTK Query's streaming updates are: a drop of
      // 250 is 250 pushes, never 250 list reads. One that fails its schema is
      // never half applied: the list reads again, and the header's listener
      // logs it (useReceiptUploadSocketSync, which also marks stale what a
      // finished upload or a reconnect changed).
      async onCacheEntryAdded(_arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        await cacheDataLoaded;
        const changed = (message: { payload: unknown }) => {
          const parsed = ReceiptUploadChangedSchema.safeParse(message.payload);
          if (!parsed.success) {
            dispatch(receiptsApi.util.invalidateTags(['ReceiptUploads']));
            return;
          }
          const { upload } = parsed.data;
          updateCachedData((uploads) => {
            const listed = uploads.findIndex((each) => each.id === upload.id);
            if (listed === -1) uploads.push(upload);
            else uploads[listed] = upload;
          });
        };
        const unsubscribe = subscribeToSocket((socket) => {
          socket.on(RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED, changed);
          return () => socket.off(RECEIPT_SOCKET_EVENTS.UPLOAD_CHANGED, changed);
        });
        await cacheEntryRemoved;
        unsubscribe();
      },
    }),

    // no responseSchema — the page reads only success, and the banners' list is read again.
    dismissReceiptUpload: builder.mutation<void, number>({
      query: (uploadId) => ({ url: RECEIPT_ROUTES.uploadDismiss(uploadId), method: 'POST' }),
      invalidatesTags: ['ReceiptUploads'],
    }),

    // Writes every receipt of the batch that needs nothing from the vendor,
    // judged against the whole batch; answers whether any still needs the screens.
    settleBatch: builder.mutation<BatchSettled, void>({
      query: () => ({ url: RECEIPT_ROUTES.batchSettle, method: 'POST' }),
      rawResponseSchema: BatchSettledEnvelopeSchema,
      transformResponse: unwrapBatchSettled,
      responseSchema: BatchSettledSchema,
      invalidatesTags: ['Batch', 'ReadyReceipts', 'Receipts'],
      // The settle takes no argument, so only the lifecycle (the second parameter) is bound.
      onQueryStarted: async (...[, { dispatch, queryFulfilled }]) => {
        try {
          await queryFulfilled;
        } catch (err) {
          // The page shows the refusal; a refused settle wrote nothing.
          log.warn({ err }, "settling the batch's receipts failed; nothing was written");
          return;
        }
        invalidateInventoryAndListings(dispatch);
      },
    }),

    // Add to inventory: writes the whole batch with the vendor's answers.
    addToInventory: builder.mutation<BatchSettled, AddToInventoryRequest>({
      query: (body) => ({ url: RECEIPT_ROUTES.batch, method: 'POST', body }),
      rawResponseSchema: BatchSettledEnvelopeSchema,
      transformResponse: unwrapBatchSettled,
      responseSchema: BatchSettledSchema,
      invalidatesTags: ['Batch', 'ReadyReceipts', 'Receipts', 'TaxPaid'],
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          // The page shows the refusal; a refused Add to inventory wrote nothing.
          log.warn({ err, newItems: body.newItems.length }, 'adding the batch to the inventory was refused; the inventory is as it was');
          return;
        }
        invalidateInventoryAndListings(dispatch);
      },
    }),

    // One signature for the settled receipts named: the list and the tax paid change, the gear does not.
    signReceipts: builder.mutation<SignedReceipts, SignReceiptsRequest>({
      query: (body) => ({ url: RECEIPT_ROUTES.signature, method: 'POST', body }),
      rawResponseSchema: SignedReceiptsEnvelopeSchema,
      transformResponse: unwrapSignedReceipts,
      responseSchema: SignedReceiptsSchema,
      invalidatesTags: ['Receipts', 'TaxPaid', 'Batch', 'ReadyReceipts'],
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          // The page shows the refusal; a refused signature signed nothing.
          log.warn({ err, receipts: body.receiptIds.length }, 'signing the receipts failed; nothing was signed');
          return;
        }
        // The Inventory page's sales-tax filter reads what the signatures show.
        dispatch(inventoryItemsApi.util.invalidateTags(['Inventory', 'InventoryDetails']));
      },
    }),

    // Deletes one of the vendor's receipts, signed too (docs/plans/receipts-redesign.md,
    // Decision 81): the list, the batch, the sign screen, the tax paid and the banners'
    // counts change (its upload file goes with it), and so does the Inventory page's
    // sales-tax filter, as after a signature. The gear it added does not.
    // no responseSchema — the page reads only success, and what the delete changed is read again.
    deleteReceipt: builder.mutation<void, number>({
      query: (receiptId) => ({ url: RECEIPT_ROUTES.receipt(receiptId), method: 'DELETE' }),
      invalidatesTags: ['Receipts', 'TaxPaid', 'Batch', 'ReadyReceipts', 'ReceiptUploads'],
      onQueryStarted: async (receiptId, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (err) {
          // The page shows the refusal; a refused delete deleted nothing.
          log.warn({ err, receiptId }, 'deleting the receipt failed; nothing was deleted');
          return;
        }
        dispatch(inventoryItemsApi.util.invalidateTags(['Inventory', 'InventoryDetails']));
      },
    }),
  }),
});

/**
 * An upload's last file was read: its job settled the batch on the server
 * (Decision 22), so everything the settle changes is read again, as after
 * the settle's own mutation. The upload list is not: the push that told of
 * the finish carried the upload. Called by the header's listener
 * (useReceiptUploadSocketSync). Below the slice, which it reads.
 */
export function refreshAfterUploadRead(dispatch: Dispatch) {
  dispatch(receiptsApi.util.invalidateTags(['Batch', 'ReadyReceipts', 'Receipts', 'TaxPaid']));
  invalidateInventoryAndListings(dispatch);
}

/** The whole batch, none set aside, none showing no payment used, no file kept and no line named: what Tax Compliance counts and Check receipts asks. */
export const WHOLE_BATCH: BatchQuery = { wait: [], use: [], keep: [], named: [] };

export const {
  useGetReceiptsQuery,
  useGetReceiptQuery,
  useGetReceiptFileQuery,
  useGetTaxPaidQuery,
  useGetBatchQuery,
  useLazyGetBatchQuery,
  useGetReadyReceiptsQuery,
  useUploadReceiptsMutation,
  useGetReceiptUploadsQuery,
  useDismissReceiptUploadMutation,
  useSettleBatchMutation,
  useAddToInventoryMutation,
  useSignReceiptsMutation,
  useDeleteReceiptMutation,
} = receiptsApi;
